import { ChevronDown } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

type AccordionType = "single" | "multiple";

interface AccordionContextValue {
  type: AccordionType;
  collapsible: boolean;
  openValues: string[];
  toggle: (value: string) => void;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(
  null
);

interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: AccordionType;
  collapsible?: boolean;
  value?: string | string[];
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
}

function toArray(value?: string | string[]) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      className,
      type = "single",
      collapsible = false,
      value,
      defaultValue,
      onValueChange,
      children,
      ...props
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<string[]>(
      toArray(defaultValue)
    );
    const openValues = isControlled ? toArray(value) : internalValue;

    const emitChange = React.useCallback(
      (next: string[]) => {
        if (!isControlled) setInternalValue(next);
        if (onValueChange) {
          onValueChange(type === "single" ? (next[0] ?? "") : next);
        }
      },
      [isControlled, onValueChange, type]
    );

    const toggle = React.useCallback(
      (itemValue: string) => {
        const isOpen = openValues.includes(itemValue);

        if (type === "single") {
          if (isOpen) {
            if (collapsible) emitChange([]);
            return;
          }
          emitChange([itemValue]);
          return;
        }

        if (isOpen) {
          emitChange(openValues.filter((v) => v !== itemValue));
          return;
        }
        emitChange([...openValues, itemValue]);
      },
      [collapsible, emitChange, openValues, type]
    );

    return (
      <AccordionContext.Provider
        value={{ type, collapsible, openValues, toggle }}
      >
        <div ref={ref} className={cn("tw-w-full", className)} {...props}>
          {children}
        </div>
      </AccordionContext.Provider>
    );
  }
);
Accordion.displayName = "Accordion";

interface AccordionItemContextValue {
  value: string;
  isOpen: boolean;
  toggle: () => void;
}

const AccordionItemContext =
  React.createContext<AccordionItemContextValue | null>(null);

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, value, children, ...props }, ref) => {
    const context = React.useContext(AccordionContext);
    if (!context) {
      throw new Error("AccordionItem must be used inside Accordion");
    }

    const isOpen = context.openValues.includes(value);

    return (
      <AccordionItemContext.Provider
        value={{
          value,
          isOpen,
          toggle: () => context.toggle(value),
        }}
      >
        <div
          ref={ref}
          className={cn("tw-border-b", className)}
          data-state={isOpen ? "open" : "closed"}
          {...props}
        >
          {children}
        </div>
      </AccordionItemContext.Provider>
    );
  }
);
AccordionItem.displayName = "AccordionItem";

type AccordionTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ className, children, ...props }, ref) => {
  const itemContext = React.useContext(AccordionItemContext);
  if (!itemContext) {
    throw new Error("AccordionTrigger must be used inside AccordionItem");
  }

  return (
    <div className="tw-flex">
      <button
        type="button"
        ref={ref}
        className={cn(
          "tw-flex tw-flex-1 tw-items-center tw-justify-between tw-py-4 tw-font-medium tw-transition-all hover:tw-underline",
          className
        )}
        onClick={(event) => {
          props.onClick?.(event);
          if (!event.defaultPrevented) itemContext.toggle();
        }}
        data-state={itemContext.isOpen ? "open" : "closed"}
        {...props}
      >
        {children}
        <ChevronDown
          className={cn(
            "tw-h-4 tw-w-4 tw-shrink-0 tw-transition-transform tw-duration-200",
            itemContext.isOpen && "tw-rotate-180"
          )}
        />
      </button>
    </div>
  );
});
AccordionTrigger.displayName = "AccordionTrigger";

type AccordionContentProps = React.HTMLAttributes<HTMLDivElement>;

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ className, children, ...props }, ref) => {
  const itemContext = React.useContext(AccordionItemContext);
  if (!itemContext) {
    throw new Error("AccordionContent must be used inside AccordionItem");
  }

  if (!itemContext.isOpen) return null;

  return (
    <div
      ref={ref}
      className={cn("tw-overflow-hidden tw-text-sm", className)}
      data-state="open"
      {...props}
    >
      <div className="tw-pb-4 tw-pt-0">{children}</div>
    </div>
  );
});
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
